import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface FormatPhoneFromPartsParams {
  internationalCountryCallingCode: string;
  phone: string;
}

export interface FormatPhoneWithMaskingFromPartsParams extends FormatPhoneFromPartsParams {
  /**
   * Covers all but the last four characters of the number before it is formatted
   * (`'8508557623'` -> `'+1 ******7623'`). Left out or false, the number is formatted in full.
   *
   * Pass it where the raw number is read - an API route, a server action - so the covered string
   * is what crosses the wire. Covering in the browser hides nothing: the full number is already in
   * the response body and the page source by the time it is drawn.
   *
   * Deliberately only on this formatter: `formatPhoneFromParts` drops a number it cannot parse, so
   * a covering caller reaching for it would silently lose the line instead of showing a covered one.
   */
  masked?: boolean;
}

/**
 * Covers all but the last four characters of a phone number, the way the stored procedures cover
 * the numbers they hand to a caller who is only picking someone (`'8508557623'` -> `'******7623'`).
 *
 * Some SPs answer with the number already covered and some answer with it in full, so this is
 * written to be idempotent: running it over a number it already covered gives the same string back.
 *
 * Deliberately not exported - callers ask for a covered number through the `masked` flag on
 * `formatPhoneWithMaskingFromParts`, so the covering and the formatting stay in step. Covering is a
 * server-side step: see the flag's own note.
 */
function coverPhoneDigits(phone?: string | null): string {
  const trimmed = (phone ?? '').trim();

  if (!trimmed) {
    return '';
  }

  const VISIBLE_TRAILING_CHARACTERS = 4;

  if (trimmed.length <= VISIBLE_TRAILING_CHARACTERS) {
    return '*'.repeat(trimmed.length);
  }

  return (
    '*'.repeat(trimmed.length - VISIBLE_TRAILING_CHARACTERS) +
    trimmed.slice(-VISIBLE_TRAILING_CHARACTERS)
  );
}

/**
 * Joins a calling code and a phone number that libphonenumber cannot format - a covered number, or
 * one that simply does not parse. The calling code gets a leading `+` if it has none, and a number
 * that arrived without a calling code is returned on its own.
 */
function withCallingCode(internationalCountryCallingCode: string, phone: string): string {
  const callingCode = (internationalCountryCallingCode ?? '').trim();

  if (!callingCode || !phone) {
    return phone;
  }

  const prefix = callingCode.startsWith('+') ? callingCode : `+${callingCode}`;

  return `${prefix} ${phone}`;
}

/**
 * Formats a phone number from separate calling code and phone number parts.
 * Use when the API returns `internationalCountryCallingCode` and `phone` as separate fields.
 *
 * Returns internationally formatted phone number string, or '' if invalid/missing.
 *
 * @example
 * // Bosnia (+387)
 * formatPhoneFromParts({ internationalCountryCallingCode: '387', phone: '61125544' })
 * // → "+387 61 125 544"
 *
 * @example
 * // USA (+1)
 * formatPhoneFromParts({ internationalCountryCallingCode: '1', phone: '2025551234' })
 * // → "+1 202 555 1234"
 *
 * @example
 * // Canada (+1)
 * formatPhoneFromParts({ internationalCountryCallingCode: '1', phone: '6135551234' })
 * // → "+1 613 555 1234"
 *
 * @example
 * // Handles '+' prefix already present
 * formatPhoneFromParts({ internationalCountryCallingCode: '+387', phone: '61125544' })
 * // → "+387 61 125 544"
 *
 * @example
 * // Invalid or missing input
 * formatPhoneFromParts({ internationalCountryCallingCode: '', phone: '61125544' })
 * // → ""
 */
export function formatPhoneFromParts({
  internationalCountryCallingCode,
  phone,
}: FormatPhoneFromPartsParams): string {
  try {
    if (!internationalCountryCallingCode || !phone) {
      return '';
    }

    const prefix = internationalCountryCallingCode.startsWith('+')
      ? internationalCountryCallingCode
      : `+${internationalCountryCallingCode}`;
    const fullNumber = `${prefix}${phone.trim()}`;
    const phoneNumber = parsePhoneNumberFromString(fullNumber);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return '';
    }

    return phoneNumber.formatInternational();
  } catch {
    return '';
  }
}

/**
 * Formats a phone number the way `formatPhoneFromParts` does, but keeps numbers that cannot be
 * parsed readable instead of dropping them.
 *
 * Some stored procedures answer with the number already covered for the caller (e.g. the league
 * partner search returns `Phone: '******3559'`). libphonenumber rejects those, so
 * `formatPhoneFromParts` returns '' and the row loses its phone line entirely. This falls back to
 * the calling code followed by the digits exactly as they arrived.
 *
 * Pass `masked: true` for the SPs that answer with the number in full but whose rows are still only
 * being picked from - the number is covered before it is formatted, and it is the covered string
 * that is sent on to the browser.
 *
 * @example
 * // A masked number, kept readable
 * formatPhoneWithMaskingFromParts({ internationalCountryCallingCode: '+1', phone: '******3559' })
 * // → "+1 ******3559"
 *
 * @example
 * // A full number the caller is not allowed to see
 * formatPhoneWithMaskingFromParts({ internationalCountryCallingCode: '1', phone: '8508557623', masked: true })
 * // → "+1 ******7623"
 *
 * @example
 * // A real number, formatted as usual
 * formatPhoneWithMaskingFromParts({ internationalCountryCallingCode: '1', phone: '2025551234' })
 * // → "+1 202 555 1234"
 *
 * @example
 * // Nothing to show
 * formatPhoneWithMaskingFromParts({ internationalCountryCallingCode: '+1', phone: '' })
 * // → ""
 */
export function formatPhoneWithMaskingFromParts({
  internationalCountryCallingCode,
  phone,
  masked = false,
}: FormatPhoneWithMaskingFromPartsParams): string {
  const trimmedPhone = (phone ?? '').trim();

  if (!trimmedPhone) {
    return '';
  }

  if (masked) {
    return withCallingCode(internationalCountryCallingCode, coverPhoneDigits(trimmedPhone));
  }

  const formatted = formatPhoneFromParts({ internationalCountryCallingCode, phone: trimmedPhone });

  if (formatted) {
    return formatted;
  }

  return withCallingCode(internationalCountryCallingCode, trimmedPhone);
}

/**
 * Validates a phone number built from separate calling code and phone number parts.
 *
 * Shares the normalization used by `formatPhoneFromParts`: the calling code gets a leading
 * `+` if it does not already have one, is concatenated with the trimmed phone, and the result
 * is parsed with `parsePhoneNumberFromString`.
 *
 * Returns false when either part is missing, so callers that accept an empty phone (e.g. a
 * minor saving without one) should short-circuit before calling this.
 *
 * @example
 * // Bosnia (+387)
 * isValidPhoneFromParts({ internationalCountryCallingCode: '387', phone: '61125544' })
 * // -> true
 *
 * @example
 * // Too short to be a real US number
 * isValidPhoneFromParts({ internationalCountryCallingCode: '1', phone: '123' })
 * // -> false
 */
export function isValidPhoneFromParts({
  internationalCountryCallingCode,
  phone,
}: FormatPhoneFromPartsParams): boolean {
  return formatPhoneFromParts({ internationalCountryCallingCode, phone }) !== '';
}

/**
 * Formats an already-concatenated phone number string (calling code + phone).
 * Use when the API returns the full phone number as a single string.
 *
 * Returns internationally formatted phone number string, or '' if invalid/missing.
 *
 * @example
 * // Bosnia (+387)
 * formatPhoneFromString('38761125544')
 * // → "+387 61 125 544"
 *
 * @example
 * // USA (+1)
 * formatPhoneFromString('12025551234')
 * // → "+1 202 555 1234"
 *
 * @example
 * // Canada (+1)
 * formatPhoneFromString('16135551234')
 * // → "+1 613 555 1234"
 *
 * @example
 * // Handles '+' prefix already present
 * formatPhoneFromString('+38761125544')
 * // → "+387 61 125 544"
 *
 * @example
 * // Invalid or missing input
 * formatPhoneFromString('')
 * // → ""
 */
export function formatPhoneFromString(fullPhoneNumber: string): string {
  try {
    if (!fullPhoneNumber) {
      return '';
    }

    const normalized = fullPhoneNumber.startsWith('+') ? fullPhoneNumber : `+${fullPhoneNumber}`;
    const phoneNumber = parsePhoneNumberFromString(normalized);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return '';
    }

    return phoneNumber.formatInternational();
  } catch {
    return '';
  }
}

/**
 * Minimum number of digits required after the leading `+` before a phone search is worth firing.
 *
 * The backend matches on a prefix (`LIKE 'term%'`), so a bare country calling code such as `+1`
 * is a valid term that matches a very large number of accounts. Callers use this to keep the UI
 * from asking for that.
 */
export const PHONE_SEARCH_MIN_DIGITS = 5;

/**
 * True when a user search term should be treated as a phone number.
 *
 * The leading `+` is the switch: the backend matches a `+`-prefixed term against the stored
 * phone number only, and does not search name, email or UUID for it.
 *
 * @example
 * isPhoneSearchTerm('  +1305555  ') // → true
 * isPhoneSearchTerm('13055550123') // → false (name / email / UUID search)
 */
export function isPhoneSearchTerm(term: string): boolean {
  return term.trim().startsWith('+');
}

/**
 * Normalizes a user search term for the `search` query param.
 *
 * Phone terms (leading `+`) are reduced to `+` plus digits, because the backend does not strip
 * formatting characters — a pasted `+1 305 555-0123` would otherwise match nothing. Everything
 * else is passed through trimmed and unchanged.
 *
 * @example
 * normalizeUserSearchTerm('+1 (305) 555-0123') // → "+13055550123"
 *
 * @example
 * normalizeUserSearchTerm('  John Doe  ') // → "John Doe"
 */
export function normalizeUserSearchTerm(term: string): string {
  const trimmed = term.trim();

  if (!isPhoneSearchTerm(trimmed)) {
    return trimmed;
  }

  return `+${trimmed.slice(1).replace(/\D/g, '')}`;
}

/**
 * Number of digits a phone search term carries after the leading `+`.
 *
 * Returns 0 for terms that are not phone terms.
 *
 * @example
 * countPhoneSearchDigits('+1 305 555') // → 7
 * countPhoneSearchDigits('John Doe') // → 0
 */
export function countPhoneSearchDigits(term: string): number {
  if (!isPhoneSearchTerm(term)) {
    return 0;
  }

  return normalizeUserSearchTerm(term).length - 1;
}

/**
 * True when a phone search term carries at least `PHONE_SEARCH_MIN_DIGITS` digits.
 *
 * Non-phone terms return false — check `isPhoneSearchTerm` first.
 *
 * @example
 * hasEnoughPhoneSearchDigits('+13055') // → true
 * hasEnoughPhoneSearchDigits('+1') // → false
 */
export function hasEnoughPhoneSearchDigits(term: string): boolean {
  return countPhoneSearchDigits(term) >= PHONE_SEARCH_MIN_DIGITS;
}
